/**
 * A WRITE THAT CHANGES THE NAME ON A CLASS-DOCUMENT ROW INVALIDATES THE NAME STAMPS.
 *
 * `placeholderName`, `pseudonymised` and `isNicknameOnly` are facts about the name currently on the
 * row: "this is a placeholder, not the child's name". The vault now TRUSTS them — that was the point
 * of round thirteen, and it is what lets the conversion stop guessing from word pools.
 *
 * Nothing invalidated them. So a stamp outlived the name it described:
 *
 *   A co-teacher sanitises a class and the seal is withheld (a school class, a co-teacher refusal, a
 *   failed student update). The rows are stamped `pseudonymised` and the class is NOT `privacyFirst`,
 *   so name edits still land on the class document. The owner types the real name back over
 *   "Amber Ash". Later they turn Privacy First on — and `namesAlreadySafe` reads the stale stamp,
 *   reports nothing to convert, and shows a green "Protected" banner over a real name with Convert
 *   never offered.
 *
 * Inside a `privacyFirst` class this cannot happen: name edits are routed to the vault and never
 * reach the row. It is the unsealed-but-stamped window that needs closing — and it needs closing
 * BEFORE any backfill stamps thousands of rows, because a stamp with no invalidation path is just
 * inference deferred.
 *
 * Only on an ACTUAL change: re-saving the same name must not clear a stamp that is still true.
 */
const NAME_STAMPS = ['placeholderName', 'pseudonymised', 'isNicknameOnly'] as const;

export function clearNameStampsIfRenamed<T extends Record<string, any>>(
  before: Record<string, any>,
  after: T,
): T {
  const changed = (['firstName', 'lastName'] as const).some((f) => {
    if (!(f in after)) return false;
    return String(after[f] ?? '').trim() !== String(before[f] ?? '').trim();
  });
  if (!changed) return after;
  const out = { ...after } as Record<string, any>;
  for (const stamp of NAME_STAMPS) delete out[stamp];
  return out as T;
}


